class CouponOfferList extends HTMLElement {
  constructor() {
    super();
    this.errorElement = this.querySelector('.mini-cart__coupon-error');
    this.addEventListener('click', this.onActionClick.bind(this));
  }

  connectedCallback() {
    // cart.cart_level_discount_applications (Liquid) does not reflect a code
    // applied via /cart/update.js — that field only reflects discounts
    // already active at checkout. The real pre-checkout state only exists in
    // the /cart.js JSON response's `discount_codes` array, so that's read
    // fresh here every time this element connects: on first page load, and
    // again on every re-render after our own apply/remove (a fresh
    // <coupon-offer-list> instance is created each time the mini-cart
    // section's innerHTML is swapped, and connectedCallback fires again).
    //
    // This is intentionally the ONLY source of truth — no client-side
    // "remembered code" layer on top. An earlier version tried to detect and
    // auto-repair a known Shopify cart/checkout session-sync quirk (a code
    // silently dropping from discount_codes after an incomplete checkout
    // visit) by re-applying whatever code the client last remembered. That
    // heuristic can't distinguish that quirk from a customer deliberately
    // removing the code via checkout's own real discount-removal UI — both
    // look identical from here (remembered code present, discount_codes
    // doesn't have it). Silently reviving a discount someone explicitly
    // removed is a worse bug than occasionally under-representing a rare
    // session glitch, so don't try to be clever here: reflect /cart.js
    // exactly as it is.
    this.syncFromCart();

    // connectedCallback only fires once for a given element — opening the
    // native <details> panel afterwards (without a cart mutation in between)
    // does NOT create a new instance, so without this the button states can
    // go stale. Re-check every time the panel actually opens instead.
    // .mini-cart__coupon-drawer ("View all coupons") is nested INSIDE this
    // element, not an ancestor of it - closest() would never find it.
    const drawer = this.querySelector('.mini-cart__coupon-drawer');
    if (drawer) {
      drawer.addEventListener('toggle', () => {
        if (drawer.open) this.syncFromCart();
      });
    }
  }

  syncFromCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const activeCodes = (cart.discount_codes || [])
          .filter((entry) => entry.applicable)
          .map((entry) => entry.code);

        this.applyDiscountState(activeCodes);
        this.updateCouponCodeLabel(activeCodes);
      })
      .catch(() => {
        // Non-fatal: worst case, an already-applied tier still shows Apply
        // until the next successful cart update re-syncs it.
      });
  }

  // Fills the "(CODE)" next to the footer's "Discount" row - kept in sync
  // with the same /cart.js fetch as the Apply/Remove buttons above, since
  // Liquid's section-rendered `cart` object doesn't reliably carry the
  // applied code before checkout (see connectedCallback's comment). Picks
  // the first applicable code; the footer only ever attributes one code's
  // name to the aggregate cart.total_discount amount.
  updateCouponCodeLabel(activeCodes) {
    const label = document.querySelector('.cart-footer-summary__coupon-code');
    if (!label) return;
    label.textContent = activeCodes.length ? ` (${activeCodes[0]})` : '';
  }

  applyDiscountState(activeCodes) {
    // Case-insensitive on purpose: Shopify echoes a discount code back with
    // whatever casing it was submitted with (confirmed - applying "save10"
    // returns "save10", not the admin-canonical "Save10"). So the same offer
    // can appear on the cart in a different case than our settings-driven
    // data-discount-code - typed at checkout, an automatic discount, etc.
    // An exact-match check would then miss it and wrongly leave the tier
    // showing "Apply" even though it's live on the cart.
    const active = activeCodes.map((code) => code.toLowerCase());
    this.querySelectorAll('[data-coupon-action="apply"]').forEach((button) => {
      const code = button.dataset.discountCode;
      if (code && active.includes(code.toLowerCase())) this.convertToRemove(button);
    });
  }

  convertToRemove(button) {
    button.dataset.couponAction = 'remove';
    button.classList.add('mini-cart__coupon-card-remove');
    button.removeAttribute('disabled');
    button.textContent = button.dataset.removeText;

    const card = button.closest('.mini-cart__coupon-card');
    if (card) card.classList.add('mini-cart__coupon-card--applied');
  }

  onActionClick(event) {
    const button = event.target.closest('[data-coupon-action]');
    if (!button || button.classList.contains('is-loading')) return;

    // Fully dynamic: the code is whatever Liquid rendered into this button's
    // data-discount-code, straight from your Thunder Sale tier settings -
    // never a hard-coded coupon.
    const code = button.dataset.discountCode;
    if (button.dataset.couponAction === 'remove') {
      this.removeOneDiscount(button, code);
    } else {
      this.applyDiscount(button, code);
    }
  }

  // Removes ONLY the given code from the cart, leaving every other code in
  // place - mirrors the tested removeOneDiscount(): read the cart's live
  // codes, filter this one out (case-insensitive, since Shopify echoes a
  // code back with whatever casing it was applied with), then re-send the
  // rest. The one addition over the console version is `sections` (in
  // submitDiscount), which re-renders the mini-cart so the drawer UI updates.
  async removeOneDiscount(button, codeToRemove) {
    this.startLoading(button);
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const remaining = (cart.discount_codes || [])
        .map((d) => d.code)
        .filter((code) => code.toLowerCase() !== codeToRemove.toLowerCase())
        .join(',');

      await this.submitDiscount(button, remaining);
    } catch (error) {
      this.failLoading(button, error);
    }
  }

  // Applies the given code. Tier offers are mutually exclusive (a cart
  // qualifies for one tier at a time), so any OTHER tier code currently on
  // the cart is swapped out while every non-tier code (the shopper's own,
  // automatic discounts) is preserved, then this code is added.
  async applyDiscount(button, codeToApply) {
    this.startLoading(button);
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const tierCodes = this.getKnownTierCodes().map((c) => c.toLowerCase());
      const preserved = (cart.discount_codes || [])
        .map((d) => d.code)
        .filter((code) => !tierCodes.includes(code.toLowerCase()));

      const next = [...preserved, codeToApply].join(',');
      await this.submitDiscount(button, next);
    } catch (error) {
      this.failLoading(button, error);
    }
  }

  // Every tier's data-discount-code, gathered from the buttons currently in
  // the DOM - straight from your theme settings, so the mutually-exclusive
  // swap in applyDiscount stays in sync with whatever tiers you configure.
  getKnownTierCodes() {
    return Array.from(this.querySelectorAll('[data-discount-code]'))
      .map((element) => element.dataset.discountCode)
      .filter(Boolean);
  }

  // Shared POST /cart/update.js. Its "discount" param REPLACES the cart's
  // whole code set with whatever string is sent (comma-separated / empty to
  // clear), which is exactly why apply/remove above compute the full
  // intended set first. `sections` gets the re-rendered mini-cart back in the
  // same response, like assets/cart.js does for quantity updates.
  submitDiscount(button, discount) {
    const body = JSON.stringify({
      discount,
      sections: ['mini-cart'],
      sections_url: window.location.pathname
    });

    return fetch(theme.routes.cart_update_url, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors) {
          throw new Error(typeof parsedState.errors === 'string' ? parsedState.errors : 'Cart update failed');
        }
        this.renderMiniCart(parsedState);
      });
  }

  startLoading(button) {
    button.dataset.originalText = button.textContent;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    this.hideError();
  }

  failLoading(button, error) {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    if (button.dataset.originalText != null) button.textContent = button.dataset.originalText;
    this.showError((error && error.message) || 'Cart update failed');
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.hidden = true;
  }

  renderMiniCart(parsedState) {
    const miniCart = document.getElementById('mini-cart');
    const sectionHtml = parsedState.sections && parsedState.sections['mini-cart'];

    if (miniCart && sectionHtml) {
      // Swapping innerHTML replaces the <details class="mini-cart__coupon-
      // drawer"> element itself with a fresh one from the server — which
      // never has the `open` attribute, since Liquid has no way to know it
      // should. Without restoring it, the coupon panel would silently snap
      // shut on every apply/remove that happens while it's open.
      const wasOpen = Boolean(miniCart.querySelector('.mini-cart__coupon-drawer')?.open);

      const newSection = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
      if (newSection) miniCart.innerHTML = newSection.innerHTML;

      if (wasOpen) {
        const newDrawer = miniCart.querySelector('.mini-cart__coupon-drawer');
        if (newDrawer) newDrawer.open = true;
      }
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
  }
}

customElements.define('coupon-offer-list', CouponOfferList);
